import { ArrowSquareOutIcon } from "@phosphor-icons/react";
import { startCase } from "lodash";
import moment from "moment-timezone";

import { Badge } from "@alpacahq/ui/old";
import type { TableColumn } from "@alpacahq/ui/old/datagrid";

import type { CryptoAsset, CryptoTransfer } from "~/v2/api/rest/cryptoWallet";
import { CopyButton } from "~/v2/components/common";
import { getPrettyAmount } from "~/v2/utils/formatting";
import { TransferDirection } from "../../funding/common/const";
import { AssetChainCell } from "../presentation/AssetChainCell";
import {
  createCryptoAssetSymbol,
  getTransactionLink,
  getTransferStatusBadgeProps,
  resolveAssetChainCell,
  transformAddress,
} from "../util";

const AMOUNT_MAX_FRACTION_DIGITS = 12;

export const createTransfersColumns = ({
  assetMap,
  onTransferTransactionClicked,
}: {
  onTransferTransactionClicked: (transferLink: string) => () => void;
  assetMap: Record<string, CryptoAsset>;
}): TableColumn<CryptoTransfer>[] => [
  {
    key: "explorer",
    name: "Explorer / Wallet",
    cellRenderer: (transfer) => {
      const assetSymbol = createCryptoAssetSymbol(
        transfer.asset,
        transfer.chain,
      );
      const asset = assetMap[assetSymbol];
      const transactionLink = getTransactionLink({ transfer, asset });
      const counterpartyAddress =
        transfer.direction === TransferDirection.INCOMING
          ? (transfer.from_address ?? "")
          : (transfer.to_address ?? "");

      return (
        <div className="flex min-w-0 flex-col items-start justify-center gap-1">
          {transactionLink && (
            <button
              type="button"
              onClick={onTransferTransactionClicked(transactionLink)}
              className="inline-flex items-center gap-1 font-medium text-blue-500 text-sm hover:text-blue-400"
            >
              Explorer
              <ArrowSquareOutIcon size={16} />
            </button>
          )}
          {counterpartyAddress && (
            <div className="flex items-center gap-1">
              <span className="font-mono text-gray-500 text-xs">
                {transformAddress(counterpartyAddress)}
              </span>
              <CopyButton
                value={counterpartyAddress}
                ariaLabel="Copy address"
              />
            </div>
          )}
        </div>
      );
    },
  },
  {
    key: "asset",
    name: "Asset / Chain",
    cellRenderer: ({ asset, chain }) => (
      <AssetChainCell {...resolveAssetChainCell({ asset, chain, assetMap })} />
    ),
  },
  {
    key: "amount",
    name: "Amount",
    cellRenderer: ({ amount }) => (
      <div className="font-mono text-gray-700 text-sm">
        {getPrettyAmount(amount, 0, AMOUNT_MAX_FRACTION_DIGITS)}
      </div>
    ),
  },
  {
    key: "type",
    name: "Type",
    cellRenderer: ({ direction }) => (
      <div className="text-gray-700 text-sm">
        {direction === TransferDirection.INCOMING ? "Deposit" : "Withdrawal"}
      </div>
    ),
  },
  {
    key: "status",
    name: "Status",
    cellRenderer: ({ status }) => (
      <Badge {...getTransferStatusBadgeProps(status)}>
        {startCase(status?.toLowerCase())}
      </Badge>
    ),
  },
  {
    key: "created_at",
    name: "Completed",
    cellRenderer: ({ created_at }) => (
      <div className="flex flex-col items-start justify-center gap-1">
        <div className="font-mono text-gray-700 text-sm">
          {moment(created_at).format("MM/DD/YYYY")}
        </div>
        <div className="font-mono text-gray-500 text-xs">
          {moment(created_at).tz("America/New_York").format("hh:mma")} ET
        </div>
      </div>
    ),
  },
];
